import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {
    Card,
    Typography,
    Button,
    Spin,
    App,
    Row,
    Col,
    Space,
    Tag,
    List,
    Divider,
    Empty,
    Modal,
    Input
} from 'antd';
import {authService, salesService} from '../../services/api';
import {usePageTitle} from '../../context/PageTitleContext';
import ErrorPage from "../ErrorPage/ErrorPage";
import {IconDotFilled, IconDotPartially, IconDotUnfilled, IconEdit} from "../../../../components/icons";
import SaleReturnModal from "../../components/SaleReturnModal";
import FinancialRefundModal from "../../components/FinancialRefundModal";
import FinancialChargeModal from "../../components/FinancialChargeModal";
import {v4 as uuidv4} from "uuid";
const { TextArea } = Input;


// Функция для форматирования даты в формате "1 июля в 23:11"
const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';

    const date = new Date(dateString);
    const months = [
        'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
        'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'
    ];

    const day = date.getDate();
    const month = months[date.getMonth()];
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day} ${month} в ${hours}:${minutes}`;
};

// Функция для форматирования суммы
const formatAmount = (amount) => {
    if (!amount) return '0.00';
    return new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
};

// Функция для получения правильного окончания слова "позиция"
const getItemsWord = (count) => {
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
        return 'позиций';
    }

    if (lastDigit === 1) {
        return 'позиция';
    }

    if (lastDigit >= 2 && lastDigit <= 4) {
        return 'позиции';
    }

    return 'позиций';
};

// Иконки для статусов
const StatusIcons = {
    IconDotFilled: IconDotFilled,
    IconDotPartially: IconDotPartially,
    IconDotUnfilled: IconDotUnfilled,
};

// Функция для рендеринга статуса оплаты
const renderPaymentStatus = (status) => {
    if (!status) return null;

    const statusConfig = {
        'paid': { color: 'default', text: 'Оплачено', icon: StatusIcons.IconDotFilled },
        'partially_refunded': { color: 'purple', text: 'Частично возвращено', icon: StatusIcons.IconDotPartially },
        'refunded': { color: 'default', text: 'Возвращено', icon: StatusIcons.IconDotFilled },
        'pending': { color: 'orange', text: 'Ожидается оплата', icon: StatusIcons.IconDotUnfilled }
    };

    const config = statusConfig[status];
    if (!config) return null;

    const IconComponent = config.icon;

    return (
        <Tag color={config.color} icon={<IconComponent />}>
            {config.text}
        </Tag>
    );
};

// Функция для рендеринга статуса выполнения
const renderFulfillmentStatus = (status) => {
    if (!status) return null;

    const statusConfig = {
        'fulfilled': { color: 'default', text: 'Собрано', icon: StatusIcons.IconDotFilled },
        'unfulfilled': { color: 'gold', text: 'Не собрано', icon: StatusIcons.IconDotFilled },
        'partially_fulfilled': { color: 'orange', text: 'Частично собрано', icon: StatusIcons.IconDotPartially },
        'scheduled': { color: 'purple', text: 'Ожидает сборки', icon: StatusIcons.IconDotUnfilled }
    };

    const config = statusConfig[status];
    if (!config) return null;

    const IconComponent = config.icon;

    return (
        <Tag color={config.color} icon={<IconComponent />}>
            {config.text}
        </Tag>
    );
};

// Функция для рендеринга статуса возврата
const renderReturnStatus = (status) => {
    if (!status) return null;

    const statusConfig = {
        'is_progress': { color: 'blue', text: 'Возврат в процессе', icon: StatusIcons.IconDotUnfilled },
        'requested': { color: 'blue', text: 'Возврат запрошен', icon: StatusIcons.IconDotUnfilled },
        'closed': { color: 'default', text: 'Возврат завершён', icon: StatusIcons.IconDotFilled }
    };

    const config = statusConfig[status];
    if (!config) return null;

    const IconComponent = config.icon;

    return (
        <Tag color={config.color} icon={<IconComponent />}>
            {config.text}
        </Tag>
    );
};

// Функция для рендеринга статуса чека
const renderReceiptStatus = (status) => {
    const statusConfig = {
        'completed': 'Подтверждён',
        'pending': 'Ожидает подтверждения',
        'failed': 'Ошибка при отправке',
        'no_data': 'Пробит, нет данных'
    };

    return statusConfig[status] || status;
};

const {Title, Text} = Typography;

const SalePage = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const {message} = App.useApp();
    const {setPageTitle, setRightContent} = usePageTitle();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saleDetails, setSaleDetails] = useState(null);
    const [printingReceipt, setPrintingReceipt] = useState(null);
    const [returnModalVisible, setReturnModalVisible] = useState(false);
    const [financialRefundModalVisible, setFinancialRefundModalVisible] = useState(false);
    const [financialChargeModalVisible, setFinancialChargeModalVisible] = useState(false);
    const [receiptModalVisible, setReceiptModalVisible] = useState(false);
    const [receiptHtml, setReceiptHtml] = useState('');
    const [noteModalVisible, setNoteModalVisible] = useState(false);
    const [noteValue, setNoteValue] = useState('');
    const [savingNote, setSavingNote] = useState(false);

    useEffect(() => {
        fetchData();
        setPageTitle('Просмотр продажи');

        // Вынесем установку rightContent в отдельный useEffect
        return () => {
            setPageTitle('');
            setRightContent(null);
            setSaleDetails(null);
        };
    }, [setPageTitle, setRightContent, id, navigate]);

// Отдельный useEffect для установки rightContent после загрузки данных
    useEffect(() => {
        if (saleDetails) {

            setRightContent(
                <Space>
                    {authService.hasPermission('pos:sales:edit') ? (
                        <>
                            <Button
                                onClick={() => setFinancialChargeModalVisible(true)}
                                ghost
                            >
                                Начисление фин.
                            </Button>
                            <Button
                                onClick={() => setFinancialRefundModalVisible(true)}
                                ghost
                            >
                                Возврат фин.
                            </Button>
                            <Button
                                onClick={handleReturnClick}
                                ghost
                            >
                                Возврат
                            </Button>
                        </>
                    ) : null}
                </Space>
            );
        }
    }, [saleDetails, setRightContent]);

    const fetchData = async () => {
        try {
            setLoading(true);

            const response = await salesService.getSaleById(id);

            if (response && response.result === 'success') {
                setSaleDetails(response.data);
                console.log('Получены данные продажи:', response.data);
            } else {
                message.error('Не удалось загрузить данные продажи');
                navigate('/pos/sales');
            }
        } catch (error) {
            if (error.response?.status === 404) {
                setError({
                    statusCode: 404,
                    title: "Продажа не найдена",
                    description: "Указанная продажа не существует или была удалена."
                });
            } else {
                setError({
                    statusCode: error.response?.status || 500,
                    title: "Ошибка загрузки данных",
                    description: "Произошла ошибка при загрузке данных продажи. Попробуйте обновить страницу или вернуться позже."
                });
            }
        } finally {
            setLoading(false);
        }
    };

    // Функция для получения чека
    const fetchReceipt = async (receiptId, receiptStatus) => {
        try {
            setPrintingReceipt(receiptId);

            if (receiptStatus !== 'completed') {
                message.warning('Чек можно распечатать только после подтверждения');
                return;
            }

            const response = await salesService.getReceipt(receiptId);

            if (response && response.result === 'success') {
                setReceiptHtml(response.receipt);
                setReceiptModalVisible(true);
            } else {
                message.error(response?.message || 'Не удалось получить чек');
            }
        } catch (error) {
            message.error(error.message || 'Ошибка при получении чека');
        } finally {
            setPrintingReceipt(null);
        }
    };

    const handleNoteSave = async () => {
        const noteData = {
            note: noteValue
        };


        const correlationKey = uuidv4();
        const idempotencyKey = uuidv4();

        try {
            setSavingNote(true);
            await salesService.editNote(sale.id, noteData, correlationKey, idempotencyKey);
            message.success('Заметка обновлена');
            setNoteModalVisible(false);
            fetchData(); // Обновляем данные
        } catch (error) {
            message.error('Ошибка при сохранении заметки');
        } finally {
            setSavingNote(false);
        }
    };

    const handleNoteEdit = () => {
        setNoteValue(note || '');
        setNoteModalVisible(true);
    };

    // Функция для открытия модала возврата
    const handleReturnClick = () => {
        setReturnModalVisible(true);
    };

    // Функция для успешного создания возврата
    const handleReturnSuccess = () => {
        fetchData(); // Обновляем данные продажи
    };

    if (loading) {
        return (
            <div style={{textAlign: 'center', padding: '50px 0'}}>
                <Spin size="large"/>
            </div>
        );
    }

    if (error) {
        return <ErrorPage
            statusCode={error.statusCode}
            title={error.title}
            description={error.description}
        />;
    }

    if (!saleDetails) {
        return (
            <div style={{textAlign: 'center', padding: '50px 0'}}>
                <Empty description="Данные продажи не найдены" />
            </div>
        );
    }

    const { sale, payments, receipts, refunds, cashier, platform_order, note, customer } = saleDetails;

    return (
        <div>
            <Title level={3} style={{marginTop: 0}}>
                Продажа #{sale.id}
            </Title>
            <Space>
                {renderPaymentStatus(payments.payment_status)}
                {renderFulfillmentStatus(sale.fulfillment_status)}
                {renderReturnStatus(sale.return_status)}
                <Text>Дата: {formatDate(sale.sale_date)}</Text>
                <Text>Кассир: {cashier.name}</Text>
            </Space>


                        <Row gutter={24}>
                            <Col span={18}>
                                <Title level={5}></Title>
                                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                    {/* Блок возвратов */}
                                    {refunds && refunds.length > 0 && (
                                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                            {refunds.map((refund, index) => (
                                                <Card key={index} title={<Space>{renderReturnStatus(refund.return_status)}<Text type="secondary">#{refund.id}, {formatDate(refund.sale_date)}</Text></Space>}>
                                                    <List
                                                        size="small"
                                                        dataSource={refund.items}
                                                        renderItem={item => (
                                                            <List.Item style={{padding: '16px 0'}}>
                                                                <div style={{
                                                                    display: 'grid',
                                                                    gridTemplateColumns: 'minmax(6.25rem, 6fr) minmax(5.625rem, auto) minmax(5rem, auto)',
                                                                    gridTemplateAreas: '"col-2 col-3 col-4"',
                                                                    gap: '16px',
                                                                    width: '100%',
                                                                    alignItems: 'start'
                                                                }}>
                                                                    <div style={{
                                                                        gridArea: 'col-2',
                                                                        wordWrap: 'break-word',
                                                                        wordBreak: 'break-word',
                                                                        lineHeight: '1.4',
                                                                        fontSize: '14px'
                                                                    }}>
                                                                        {item.id}: {item.name}
                                                                    </div>
                                                                    <div style={{
                                                                        gridArea: 'col-3',
                                                                        textAlign: 'right',
                                                                        fontWeight: '500',
                                                                        fontSize: '14px',
                                                                        whiteSpace: 'nowrap'
                                                                    }}>
                                                                        {formatAmount(item.unit_price)} ₸&nbsp;&nbsp;×&nbsp;&nbsp;{item.quantity}
                                                                    </div>
                                                                    <div style={{
                                                                        gridArea: 'col-4',
                                                                        textAlign: 'right',
                                                                        fontWeight: '500',
                                                                        fontSize: '14px',
                                                                        whiteSpace: 'nowrap'
                                                                    }}>
                                                                        {formatAmount(item.total_amount)} ₸
                                                                    </div>
                                                                </div>
                                                            </List.Item>
                                                        )}
                                                    />
                                                </Card>
                                            ))}
                                        </Space>
                                    )}

                                    {/* Блок несобранных товаров */}
                                    {sale.unfulfulled_items && sale.unfulfulled_items.length > 0 && (
                                        <>
                                            <Card title={<Space><Tag color="gold">Не собрано</Tag></Space>}>
                                                <List
                                                    size="small"
                                                    dataSource={sale.unfulfulled_items}
                                                    renderItem={item => (
                                                        <List.Item style={{padding: '16px 0'}}>
                                                            <div style={{
                                                                display: 'grid',
                                                                gridTemplateColumns: 'minmax(6.25rem, 6fr) minmax(5.625rem, auto) minmax(5rem, auto)',
                                                                gridTemplateAreas: '"col-2 col-3 col-4"',
                                                                gap: '16px',
                                                                width: '100%',
                                                                alignItems: 'start'
                                                            }}>
                                                                <div style={{
                                                                    gridArea: 'col-2',
                                                                    wordWrap: 'break-word',
                                                                    wordBreak: 'break-word',
                                                                    lineHeight: '1.4',
                                                                    fontSize: '14px'
                                                                }}>
                                                                    {item.id}: {item.name}
                                                                </div>
                                                                <div style={{
                                                                    gridArea: 'col-3',
                                                                    textAlign: 'right',
                                                                    fontWeight: '500',
                                                                    fontSize: '14px',
                                                                    whiteSpace: 'nowrap'
                                                                }}>
                                                                    {formatAmount(item.unit_price)} ₸&nbsp;&nbsp;×&nbsp;&nbsp;{item.quantity}
                                                                </div>
                                                                <div style={{
                                                                    gridArea: 'col-4',
                                                                    textAlign: 'right',
                                                                    fontWeight: '500',
                                                                    fontSize: '14px',
                                                                    whiteSpace: 'nowrap'
                                                                }}>
                                                                    {formatAmount(item.total_amount)} ₸
                                                                </div>
                                                            </div>
                                                        </List.Item>
                                                    )}
                                                />
                                            </Card>
                                        </>
                                    )}

                                    {/* Блок собранных товаров */}
                                    {sale.items && sale.items.length > 0 && (
                                        <>
                                            <Card title={<Space><Tag color="default">Собрано</Tag></Space>}>
                                                <List
                                                    size="small"
                                                    dataSource={sale.items}
                                                    renderItem={item => (
                                                        <List.Item style={{padding: '16px 0'}}>
                                                            <div style={{
                                                                display: 'grid',
                                                                gridTemplateColumns: 'minmax(6.25rem, 6fr) minmax(5.625rem, auto) minmax(5rem, auto)',
                                                                gridTemplateAreas: '"col-2 col-3 col-4"',
                                                                gap: '16px',
                                                                width: '100%',
                                                                alignItems: 'start'
                                                            }}>
                                                                <div style={{
                                                                    gridArea: 'col-2',
                                                                    wordWrap: 'break-word',
                                                                    wordBreak: 'break-word',
                                                                    lineHeight: '1.4',
                                                                    fontSize: '14px'
                                                                }}>
                                                                    {item.id}: {item.name}
                                                                </div>
                                                                <div style={{
                                                                    gridArea: 'col-3',
                                                                    textAlign: 'right',
                                                                    fontWeight: '500',
                                                                    fontSize: '14px',
                                                                    whiteSpace: 'nowrap'
                                                                }}>
                                                                    {formatAmount(item.unit_price)} ₸&nbsp;&nbsp;×&nbsp;&nbsp;{item.quantity}
                                                                </div>
                                                                <div style={{
                                                                    gridArea: 'col-4',
                                                                    textAlign: 'right',
                                                                    fontWeight: '500',
                                                                    fontSize: '14px',
                                                                    whiteSpace: 'nowrap'
                                                                }}>
                                                                {formatAmount(item.total_amount)} ₸
                                                                </div>
                                                            </div>
                                                        </List.Item>
                                                        )}
                                                    />
                                            </Card>
                                        </>
                                    )}

                                    {/* Блок оплат */}
                                    <Card title={<Space><Tag color="default">{payments.payment_status === 'paid' ? 'Оплачено' : 'Не оплачено'}</Tag></Space>}>
                                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                            <Row justify="space-between" align="middle">
                                                <Col span={6}>
                                                    <Text strong>Итого</Text>
                                                </Col>
                                                <Col flex="auto">
                                                    <Text strong>{payments.total.qty_items} {getItemsWord(payments.total.qty_items)}</Text>
                                                </Col>
                                                <Col>
                                                    <Text strong>{formatAmount(payments.total.amount)} ₸</Text>
                                                </Col>
                                            </Row>

                                            {payments.tax && payments.tax.rate && payments.tax.rate > 0 && (
                                                <Row justify="space-between" align="middle">
                                                    <Col span={6}>
                                                        <Text>Налог</Text>
                                                    </Col>
                                                    <Col flex="auto">
                                                        <Text>в т.ч. НДС {payments.tax.rate}%</Text>
                                                    </Col>
                                                    <Col>
                                                        <Text>{formatAmount(payments.tax.amount)} ₸</Text>
                                                    </Col>
                                                </Row>
                                            )}

                                            {customer.transactions && customer.transactions.map((transaction, index) => (
                                                <Row key={index} justify="space-between" align="middle">
                                                    <Col span={6}>
                                                        <Text>{index === 0 ? 'Бонусы клиенту' : ''}</Text>
                                                    </Col>
                                                    <Col flex="auto">
                                                        <Text>Транзакция #{transaction.id}</Text>
                                                    </Col>
                                                    <Col>
                                                        <Text>{formatAmount(transaction.amount)} Б</Text>
                                                    </Col>
                                                </Row>
                                            ))}

                                            <Divider style={{ margin: '8px 0' }} />

                                            {payments.paid && payments.paid.map((payment, index) => (
                                                <Row key={index} justify="space-between" align="middle">
                                                    <Col span={6}>
                                                        <Text>{index === 0 ? 'Оплачено' : ''}</Text>
                                                    </Col>
                                                    <Col flex="auto">
                                                        <Text>{payment.payment_method_name}</Text>
                                                    </Col>
                                                    <Col>
                                                        <Text>{formatAmount(payment.amount)} ₸</Text>
                                                    </Col>
                                                </Row>
                                            ))}

                                            {payments.refunded && payments.refunded.map((refund, index) => (
                                                <Row key={index} justify="space-between" align="middle">
                                                    <Col span={6}>
                                                        <Text>{index === 0 ? 'Возврат' : ''}</Text>
                                                    </Col>
                                                    <Col flex="auto">
                                                        <Text>{refund.payment_method_name}</Text>
                                                    </Col>
                                                    <Col>
                                                        <Text>{formatAmount(refund.amount)} ₸</Text>
                                                    </Col>
                                                </Row>
                                            ))}

                                            {payments.refunded && payments.refunded.length > 0 && (
                                                <Row justify="space-between" align="middle" style={{ marginTop: 8 }}>
                                                    <Col span={6}>
                                                        <Text strong>Чистая сумма оплаты</Text>
                                                    </Col>
                                                    <Col flex="auto" />
                                                    <Col>
                                                        <Text strong>{formatAmount(payments.net_payment)} ₸</Text>
                                                    </Col>
                                                </Row>
                                            )}
                                        </Space>
                                    </Card>
                                </Space>
                            </Col>
                            <Col span={6}>
                                <Title level={5}></Title>
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <>
                                        <Card size="medium">
                                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Text strong>Заметка</Text>
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={<IconEdit />}
                                                        onClick={handleNoteEdit}
                                                        style={{ padding: '4px' }}
                                                    />
                                                </div>
                                                <Text type={note ? 'default' : 'secondary'}>
                                                    {note || 'Нет заметки'}
                                                </Text>
                                            </Space>
                                        </Card>
                                    </>
                                    <>
                                        <Card size="medium">
                                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                                <div>
                                                    <Text type="secondary">Платформа:</Text>
                                                    <br />
                                                    <Text>{platform_order.platform}</Text>
                                                </div>
                                                {platform_order.platform_order_id && (
                                                    <div>
                                                        <Text type="secondary">ID у платформы:</Text>
                                                        <br />
                                                        <Text>{platform_order.platform_order_id}</Text>
                                                    </div>
                                                )}
                                                {platform_order.platform_order_uid && (
                                                    <div>
                                                        <Text type="secondary">UID у платформы:</Text>
                                                        <br />
                                                        <Text>{platform_order.platform_order_uid}</Text>
                                                    </div>
                                                )}
                                                {platform_order.platform_oms_id && (
                                                    <div>
                                                        <Text type="secondary">ID ИС:</Text>
                                                        <br />
                                                        <Text>{platform_order.platform_oms_id}</Text>
                                                    </div>
                                                )}
                                                {customer.full_name && (
                                                    <div>
                                                        <Text type="secondary">Клиент:</Text>
                                                        <br />
                                                        <Text>{customer.full_name}</Text>
                                                    </div>
                                                )}

                                                {customer.id && (
                                                    <div>
                                                        <Text type="secondary">ID клиента:</Text>
                                                        <br />
                                                        <Text>{customer.id}</Text>
                                                    </div>
                                                )}
                                            </Space>
                                        </Card>
                                    </>
                                    {receipts.map((receipt) => (
                                        <Card
                                            key={receipt.id}
                                            size="medium"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => fetchReceipt(receipt.id, receipt.status)}
                                            loading={printingReceipt === receipt.id}
                                        >
                                            <Space direction="vertical" size="small">
                                                <Text strong>
                                                    Чек {receipt.type === 'sale' ? 'продажи' : 'возврата'} #{receipt.ticket_number}
                                                </Text>
                                                <Text type="secondary">
                                                    {renderReceiptStatus(receipt.status)}
                                                </Text>
                                            </Space>
                                        </Card>
                                    ))}
                                </Space>
                            </Col>
                        </Row>
            <Modal
                title="Чек"
                open={receiptModalVisible}
                onCancel={() => setReceiptModalVisible(false)}
                footer={null}
                width={316}
                styles={{
                    body: { padding: 0 }
                }}
            >
                <iframe
                    srcDoc={receiptHtml}
                    style={{
                        width: '100%',
                        height: '40vh',
                        border: 'none'
                    }}
                    title="Чек"
                />
            </Modal>
            <SaleReturnModal
                visible={returnModalVisible}
                onClose={() => setReturnModalVisible(false)}
                saleId={sale.id}
                cashRegisterId={saleDetails.cash_register?.id}
                onSuccess={handleReturnSuccess}
            />
            <FinancialRefundModal
                visible={financialRefundModalVisible}
                onClose={() => setFinancialRefundModalVisible(false)}
                saleId={sale.id}
                cashRegisterId={saleDetails.cash_register?.id}
                onSuccess={handleReturnSuccess}
            />
            <FinancialChargeModal
                visible={financialChargeModalVisible}
                onClose={() => setFinancialChargeModalVisible(false)}
                saleId={sale.id}
                cashRegisterId={saleDetails.cash_register?.id}
                onSuccess={handleReturnSuccess}
            />
            <Modal
                title="Редактировать заметку"
                open={noteModalVisible}
                onCancel={() => setNoteModalVisible(false)}
                onOk={handleNoteSave}
                confirmLoading={savingNote}
                okText="Сохранить"
                cancelText="Отмена"
            >
                <TextArea
                    value={noteValue}
                    onChange={(e) => setNoteValue(e.target.value)}
                    placeholder="Введите заметку..."
                    maxLength={200}
                    rows={4}
                    showCount
                />
            </Modal>
        </div>
    );
}

export default SalePage;